// Single source of truth for "what may this user do with this project row?".
//
// The unified Projects table mixes three kinds of row:
//   access_scope "owned"        - the user's own project        (permission "owner")
//   access_scope "shared"       - shared with them              ("editor" | "reviewer")
//   access_scope "organization" — an org admin seeing a member's project, with no
//                                 permissions at all            ("none")
//
// The backend enforces this same split on every endpoint; these helpers exist so
// the UI never offers an action that would come back 403, and so the rule is
// written down once rather than re-derived at each call site.

// Visible for oversight only: no access to the project's contents or actions.
export const hasNoAccess = (item) =>
  item?.access_scope === "organization" ||
  String(item?.user_permission || "").toLowerCase() === "none";

// May change project fields inline (status, dates, contract value, note).
export const canEditRow = (item) =>
  !hasNoAccess(item) &&
  (item?.is_owner === true ||
    String(item?.user_permission || "").toLowerCase() === "editor");

// May rename, share, archive, delete, tag, and set the project lead.
export const isOwnerRow = (item) => item?.is_owner === true && !hasNoAccess(item);

// May remove themselves from the project — only if they were given access to it.
// An owner can't leave their own project; an oversight row was never joined.
export const canLeaveRow = (item) => !hasNoAccess(item) && item?.is_owner !== true;

// May be selected for the owner-only bulk Delete.
export const isSelectableRow = (item) => Boolean(item?.id) && isOwnerRow(item);

// May hand the project to someone else.
//
//   - the owner, handing over voluntarily
//   - a workspace admin, but ONLY once the owner has left. Rename / archive /
//     delete / share are all owner-only, so an offboarded owner leaves the
//     project frozen and a transfer is the only way to free it. Seeing a
//     colleague's project must never become a way to take it from them, so this
//     stays false while the owner is still around.
//
// The backend enforces the identical rule and is the real gate.
export const canTransferOwnership = (item) =>
  isOwnerRow(item) || (hasNoAccess(item) && item?.owner_is_former_member === true);

// How many entries the row's ⋯ menu will render, used to decide whether the
// popover opens upwards. Keep in step with ActionMenuPopover.
export const actionMenuItemCount = (item) => {
  // A locked row shows an explanatory panel; a frozen one adds a transfer action.
  if (hasNoAccess(item)) return canTransferOwnership(item) ? 1 : 0;
  if (isOwnerRow(item)) return 7; // Details, Share, Note, Rename, Transfer, Archive, Delete
  if (canEditRow(item)) return 3; // Details, Note, Leave
  return 2; // Details, Leave
};

// ─── The "My access" filter ───────────────────────────────────────────────────
// Lives in the Filters panel alongside Status and the date ranges. Shown to the
// user as "My access", named to match the table column it filters:
//
//   all           "All projects"    everything the user can see       (default)
//   owned         "Owned by me"     projects they own
//   shared        "Shared with me"  shared with them as editor/reviewer
//   organization  "No access yet"   other members' projects — org admins only,
//                                   visible for oversight with no permissions
//
// The keys keep their original names because they are persisted in localStorage
// and in saved column preferences; only the labels changed. `normalizeSource`
// below is what makes that safe.
//
// ALL is the default: the merged list is the point of the page, and for everyone
// except org admins these four resolve to almost the same rows anyway.
export const PROJECT_SOURCES = {
  ALL: "all",
  OWNED: "owned",
  SHARED: "shared",
  ORGANIZATION: "organization",
};

const VALID_SOURCES = new Set(Object.values(PROJECT_SOURCES));

// Anything unrecognised — a value persisted by an older build, a hand-edited
// localStorage entry — resolves to ALL rather than leaving the table filtered
// by a rule the UI can no longer show or undo.
export const normalizeSource = (value) =>
  VALID_SOURCES.has(value) ? value : PROJECT_SOURCES.ALL;

export const matchesSource = (item, source) => {
  switch (normalizeSource(source)) {
    case PROJECT_SOURCES.OWNED:
      return isOwnerRow(item);
    case PROJECT_SOURCES.SHARED:
      return item?.access_scope === "shared";
    case PROJECT_SOURCES.ORGANIZATION:
      return hasNoAccess(item);
    default:
      return true;
  }
};
